/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Link, Typography2, breakpoints } from '@coursera/cds-core';

import { useHeaderLevel } from 'bundles/page-config-common/contexts/HeaderLevelContext';
import { useTracker, useVisibilityTracker } from 'bundles/page/lib/event-pulse/react';

const styles = {
  pillContainer: css`
    background-color: var(--cds-color-blue-950);
    border-radius: var(--cds-border-radius-200);
    border: 1px solid var(--cds-color-grey-50);
    max-height: var(--cds-spacing-1400);
    padding: var(--cds-spacing-300) var(--cds-spacing-400);
    row-gap: var(--cds-spacing-100);
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;

    &:hover,
    :focus {
      background: var(--cds-color-blue-900);
    }

    ${breakpoints.down('sm')} {
      padding: var(--cds-spacing-50) var(--cds-spacing-400);
      row-gap: 0;
    }
  `,
  text: css`
    &:hover,
    :focus {
      text-decoration: none;
    }
  `,
};

type DomainPillItemProps = {
  id?: string | null;
  name?: string | null;
  url?: string;
  subdomain?: React.ReactNode;
  index: number;
};

export type DomainPillItemElement = React.ReactElement<DomainPillItemProps, typeof DomainPillItem>;

const DomainPillItem = ({ id, name, subdomain, url, index }: DomainPillItemProps) => {
  const track = useTracker();
  const { nextLevel } = useHeaderLevel();

  const trackingData = {
    topicId: id || '',
    topicCardIndex: index,
  };

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_topic_card',
    trackingData
  );

  if (!id) {
    return null;
  }

  return (
    <div css={styles.text} ref={visibilityTrackingRef}>
      <Link
        css={styles.pillContainer}
        variant="quiet"
        href={url}
        onClick={() => {
          track('click_topic_card', trackingData);
        }}
      >
        <Typography2 variant="subtitleLarge" component={nextLevel} color="invertBody">
          {name}
        </Typography2>
        {subdomain}
      </Link>
    </div>
  );
};

export default DomainPillItem;
